import { Sparkles } from "lucide-react";
import { useState } from "react";

import { useJDDrafts, useReviewJDDrafts, type JDDraftBatch } from "@/api/jd-gap-queries";
import { Button } from "@/components/ui/button";
import { ErrorNote } from "@/components/ui/panel";

/**
 * Questions the model drafted for this JD's gaps (DESIGN §20.6, Phase 3), waiting for a decision — the
 * top of the Questions step, because nothing else there moves until somebody decides. Kept ones become
 * the JD's `generated` questions and go through the pipeline and review like any other; the rest are
 * deleted for good — the same rule as the Ingest screen's generated review, run here so a Resource
 * Manager decides on their own JD's drafts.
 */

// Enough to judge the batch by; the rest are one click away rather than a wall of text.
const SHOWN = 4;

function Batch({ jdId, batch, canManage }: { jdId: number; batch: JDDraftBatch; canManage: boolean }) {
  const review = useReviewJDDrafts(jdId);
  const [keep, setKeep] = useState<Set<number>>(() => new Set(batch.items.map((d) => d.id)));
  const [all, setAll] = useState(false);
  const shown = all ? batch.items : batch.items.slice(0, SHOWN);

  function toggle(id: number) {
    setKeep((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  return (
    <section className="border-bank-attention bg-bank-surface overflow-hidden rounded-lg border">
      <div className="border-bank-attention bg-bank-attention-wash text-bank-attention-ink flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2.5">
        <Sparkles className="size-4" aria-hidden="true" />
        <h3 className="text-sm font-semibold">
          {batch.items.length} AI draft{batch.items.length === 1 ? "" : "s"} for {batch.skill_name ?? "a gap"}{" "}
          {batch.items.length === 1 ? "is" : "are"} waiting for you
        </h3>
        <span className="font-mono text-[11px]">
          {batch.stack} · job #{batch.job_id}
        </span>
        {canManage && <span className="ml-auto text-[12.5px]">Untick the ones to throw away</span>}
      </div>
      <ul className="px-4 py-1.5">
        {shown.map((d) => (
          <li key={d.id} className="flex items-start gap-2.5 py-1.5">
            {canManage && (
              <input
                type="checkbox"
                checked={keep.has(d.id)}
                onChange={() => toggle(d.id)}
                aria-label={`Keep draft ${d.id}`}
                className="accent-bank-accent mt-1 size-3.5 shrink-0"
              />
            )}
            <span
              className={canManage && !keep.has(d.id) ? "text-bank-muted text-sm line-through" : "text-sm"}
            >
              {d.question_text}
            </span>
          </li>
        ))}
      </ul>
      <div className="border-bank-border flex flex-wrap items-center gap-2.5 border-t px-4 py-2.5">
        {batch.items.length > SHOWN && (
          <Button size="sm" variant="ghost" onClick={() => setAll(!all)}>
            {all ? "Show fewer" : `Show ${batch.items.length - SHOWN} more`}
          </Button>
        )}
        <span className="text-bank-muted text-[12.5px]">
          Kept drafts go through the pipeline and review like any question. Discarded ones are deleted.
        </span>
        {canManage && (
          <Button
            size="sm"
            variant="primary"
            className="ml-auto"
            disabled={review.isPending}
            onClick={() => review.mutate({ jobId: batch.job_id, keepIds: [...keep] })}
          >
            {keep.size === 0 ? "Discard all" : `Keep ${keep.size}, discard ${batch.items.length - keep.size}`}
          </Button>
        )}
      </div>
      <div className="px-4 empty:hidden">
        <ErrorNote error={review.error} />
      </div>
    </section>
  );
}

export function DraftsPanel({ jdId, canManage }: { jdId: number; canManage: boolean }) {
  const drafts = useJDDrafts(jdId, false);
  const batches = drafts.data?.batches ?? [];
  if (batches.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      {batches.map((batch) => (
        <Batch key={batch.job_id} jdId={jdId} batch={batch} canManage={canManage} />
      ))}
    </div>
  );
}
